'use client';

import { Button, cn, type ButtonProps } from '@up2stay/up2promo-ui';
import { useIntercom } from '@up2promo-setup-frontend/core/hooks/use-intercom';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';
import {
  SETUP_SELECTABLE_PRODUCT_TYPES,
  usesUrlSetupFlow,
} from '@up2promo-setup-frontend/core/constants/setup-product-types';
import { type ProductType } from '@up2promo-setup-frontend/core/types/product-type.enum';
import { dictByType } from '@up2promo-setup-frontend/core/utils/dict-by-type';
import { fadeInUp } from '@up2promo-setup-frontend/ui/animations/fade-in-up.animation';
import { useDictionary } from '@up2promo-setup-frontend/ui/providers/dictionary.provider';
import { motion } from 'motion/react';
import { useCallback } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useSetupFlow } from '@up2promo-setup-frontend/core/hooks/use-setup-flow';
import { SetupStepShell } from '../components/setup-step-shell';
import { useSetupNavigation } from './hooks/use-setup-navigation';

export const ProductTypeButton = (props: ButtonProps) => {
  const { children, className, ...otherProps } = props;

  return (
    <Button
      variant="outline"
      size="lg"
      className={cn('min-h-11 w-full sm:w-auto', className)}
      {...otherProps}
    >
      {children}
    </Button>
  );
};

export default function SelectTypePage() {
  const translations = useDictionary();
  const { goNext } = useSetupNavigation();
  const { hash: hashParam } = useParams<{ hash: string }>();
  const { initialType } = useSetupFlow();

  const type = useSetupStore((state) => state.type);
  const hash = useSetupStore((state) => state.hash);
  const email = useSetupStore((state) => state.email);
  const setType = useSetupStore((state) => state.setType);
  const setInput = useSetupStore((state) => state.setInput);

  const intercom = useIntercom({
    user: hash ? { hash, email } : email ? { email } : undefined,
    enabled: true,
  });

  const saveTypeAndContinue = useCallback(
    (newType: ProductType) => {
      if (type !== newType) setInput(undefined);
      setType(newType);

      if (intercom.isBooted()) {
        intercom.trackEvent('type-selected', {
          hash,
          selected_type: newType,
          previous_type: type,
          has_email: !!email,
        });
      }

      goNext('select-type');
    },
    [type, setType, setInput, goNext, intercom, hash, email],
  );

  if (initialType) {
    const basePath = hashParam ? `/${hashParam}` : '';
    const redirectPath = usesUrlSetupFlow(initialType)
      ? `${basePath}/custom-url`
      : `${basePath}/input`;
    return <Navigate to={redirectPath} replace />;
  }

  const types = SETUP_SELECTABLE_PRODUCT_TYPES;

  return (
    <SetupStepShell>
      <div className="flex flex-col gap-2 sm:gap-3">
        <motion.h1
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="text-2xl font-semibold tracking-tight text-foreground text-balance md:text-3xl lg:text-4xl"
        >
          {translations.pages.selectType.title}
        </motion.h1>
        <motion.p
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="max-w-prose text-sm text-foreground/70 text-pretty md:text-base"
        >
          {translations.pages.selectType.description}
        </motion.p>

        <motion.div
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="my-2 flex flex-col gap-2 sm:my-4 md:my-6"
        >
          <p className="text-sm font-medium text-foreground">
            {translations.pages.selectType.optionsTitle}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {types.map((productType) => (
              <ProductTypeButton
                key={productType}
                onClick={() => saveTypeAndContinue(productType)}
              >
                {dictByType(translations.types, productType)}
              </ProductTypeButton>
            ))}
          </div>
        </motion.div>
      </div>
    </SetupStepShell>
  );
}
